import { useMemo } from 'react';

import { useSites } from '@hotjar/site';
import { useGates, useTiers } from '@hotjar/allowances';

import { useAllIntegrationsStatus } from './use-all-integrations-status';

import type { Integration } from '../types';
import { inHouseIntegrations, externalIntegrations } from '../config';
import { getAllIntegrationsConfig } from '../utils';

interface UseIntegrations {
  isLoading: boolean;
  refreshTokens: () => void;
  integrations: Integration[];
  externalIntegrations: Integration[];
}

export const useIntegrations = (siteId: number | undefined): UseIntegrations => {
  const {
    query: { data: sites },
  } = useSites();
  const site = sites?.find((s) => s.id === siteId);
  const siteIdOrNull = siteId ? String(siteId) : null;
  const gates = useGates({ id: siteIdOrNull, type: 'site' });
  const tiers = useTiers({ id: siteIdOrNull, type: 'site' });
  const { data, status, isLoading, refetch } = useAllIntegrationsStatus(siteIdOrNull);

  const integrations = useMemo(() => {
    if (!site || status !== 'success' || !data || !gates || !tiers) {
      return inHouseIntegrations;
    }

    return getAllIntegrationsConfig(site, data, { gates, tiers });
  }, [site, data, status, gates, tiers]);

  const refreshTokens = () => {
    refetch();
  };

  return {
    isLoading,
    refreshTokens,
    integrations,
    externalIntegrations,
  };
};
